import * as React from 'react'
import { useEffect, useRef } from 'react'
import type { JSX } from 'react'
import { Provider as RadixTooltipProvider } from '@radix-ui/react-tooltip'

import { PortalContainerProvider } from '../../helpers/usePortalContainer'
import { Toaster } from '../Toast/Toaster'

// global CSS with tokens
// oxlint-disable-next-line import/no-unassigned-import
import '../../styles/index.css'
import { useCookie } from '../../hooks/useCookie'

type Theme = 'light' | 'dark' | 'system'
type SystemAppearance = 'light' | 'dark'
const THEME_STORAGE_KEY = 'popcorn_theme'

export const THEMES: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
}

// ResolvedTheme represents themes that can be directly applied to the DOM.
// The 'system' value is a user preference and must be resolved to a concrete theme ('light', 'dark', etc.) before applying.
type ResolvedTheme = Exclude<Theme, 'system'>

type ThemeContextValue = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(
  undefined,
)

type PopcornProviderProps = {
  children: React.ReactNode
  /**
   * The theme to use. If not provided, the theme will be set based on the
   * user's system preferences.
   * @default 'default'
   **/
  theme?: Theme
  forcedTheme?: ResolvedTheme
  /**
   * The id of the container where the portals will be rendered
   **/
  portalContainerId?: string
  /**
   * Whether toaster should be rendered. Internal use only.
   */
  _toaster?: boolean
}

function useTheme(): ThemeContextValue {
  const context = React.useContext(ThemeContext)

  if (context === undefined) {
    throw new Error('`useTheme` must be used within a `PopcornProvider`')
  }

  return context
}

function detectSystemTheme(): SystemAppearance {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return 'light'
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

  // Defensive check: ensure mediaQuery is valid before accessing .matches
  if (!mediaQuery || typeof mediaQuery.matches !== 'boolean') {
    return 'light'
  }

  return mediaQuery.matches ? 'dark' : 'light'
}

/**
 * Resolves a theme preference to an actual theme that can be applied.
 * If theme is 'system', returns the OS preference, otherwise returns the theme as-is.
 */
function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') {
    return detectSystemTheme()
  }
  return theme
}

function detectCurrentTheme(): ResolvedTheme {
  // Check if a theme is already applied to the document (by inline script)
  const root = document.documentElement

  // Check for themes
  if (root.classList.contains('dark')) {
    return 'dark'
  }
  if (root.classList.contains('light')) {
    return 'light'
  }

  // Fallback to system preference if no theme class found
  return detectSystemTheme()
}

/**
 * Check if the current domain is an engage domain
 */
function isEngageDomain(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const hostname = window.location.hostname
  return (
    hostname === 'engage.buffer.com' ||
    hostname === 'engage.local.buffer.com' ||
    hostname === 'engage.dev.buffer.com'
  )
}

function getFallbackTheme(): Theme {
  return typeof document !== 'undefined'
    ? detectCurrentTheme()
    : detectSystemTheme()
}

// sets theme class on the root element
function setThemeStyles(newTheme: ResolvedTheme): void {
  const root = document.documentElement

  // Determine the applied theme from classes
  const hasLight = root.classList.contains('light')
  const hasDark = root.classList.contains('dark')
  const appliedTheme = hasLight ? 'light' : hasDark ? 'dark' : null

  // Only update if the theme is different to prevent unnecessary DOM changes
  if (appliedTheme !== newTheme) {
    root.classList.remove('light', 'dark')
    root.classList.add(newTheme)
    root.style.colorScheme = newTheme
  }
}

function PopcornProvider({
  children,
  theme,
  forcedTheme,
  portalContainerId,
  _toaster = true,
}: PopcornProviderProps): JSX.Element {
  // Track whether a cookie existed before this component could write one.
  // This ensures the cookie remains source of truth when it exists,
  // while still allowing theme prop changes to take effect when it doesn't.
  const hadCookieOnMount = useRef(
    typeof document !== 'undefined' &&
      document.cookie.includes(`${THEME_STORAGE_KEY}=`),
  )

  // Resolve the cookie fallback once so the setter returned by `useCookie`
  // keeps a stable identity across renders.
  const [defaultTheme] = React.useState<Theme>(
    () => theme ?? getFallbackTheme(),
  )

  const [currentTheme, setCurrentTheme] = useCookie<Theme>(
    THEME_STORAGE_KEY,
    defaultTheme,
  )

  // Sync theme prop changes only when no cookie existed on mount
  useEffect(() => {
    if (!hadCookieOnMount.current && theme !== undefined) {
      setCurrentTheme(theme)
    }
  }, [theme, setCurrentTheme])

  // Force light mode for engage domains; otherwise the cookie is source of truth
  const computedTheme: Theme =
    forcedTheme ?? (isEngageDomain() ? 'light' : currentTheme)

  // Resolve the theme to apply (convert 'system' to actual OS theme)
  const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>(() =>
    resolveTheme(computedTheme),
  )

  // Listen for OS theme changes when theme is set to 'system'
  useEffect(() => {
    if (computedTheme !== 'system') {
      setResolvedTheme(resolveTheme(computedTheme))
      return
    }

    // Initial resolution
    const resolved = resolveTheme(computedTheme)
    setResolvedTheme(resolved)

    // Listen for OS theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent): void => {
      const newResolvedTheme = e.matches ? 'dark' : 'light'
      setResolvedTheme(newResolvedTheme)
    }

    mediaQuery.addEventListener('change', handleChange)

    return (): void => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [computedTheme])

  useEffect(() => {
    setThemeStyles(resolvedTheme)
  }, [resolvedTheme])

  // Memoized so context consumers only re-render when the theme actually changes
  const themeContextValue = React.useMemo(
    () => ({
      theme: computedTheme,
      setTheme: setCurrentTheme,
    }),
    [computedTheme, setCurrentTheme],
  )

  const [container, setContainer] = React.useState<HTMLElement | null>(null)
  useEffect(() => {
    if (portalContainerId) {
      setContainer(document.getElementById(portalContainerId))
    }
  }, [portalContainerId])

  return (
    <PortalContainerProvider
      container={
        container ?? (typeof document !== 'undefined' ? document.body : null)
      }
    >
      <ThemeContext.Provider value={themeContextValue}>
        <RadixTooltipProvider>
          {children}
          {_toaster && <Toaster />}
        </RadixTooltipProvider>
      </ThemeContext.Provider>
    </PortalContainerProvider>
  )
}

export { PopcornProvider, ThemeContext, useTheme }
export type { Theme, SystemAppearance }
